import React, { useEffect, useRef, useState } from 'react';
import '../../pages/AddDataPage/styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const SchemaAddDataPanel = ({ entityId, schemaId, onClose, onSuccess }) => {
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [inputMode, setInputMode] = useState('upload');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (schemaId && entityId) {
      fetchSchema();
    }
  }, [schemaId, entityId]);

  const fetchSchema = async () => {
    if (!schemaId || !entityId) {
      setError('Schema ID and Entity ID are required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/admin/schemas/${schemaId}?entity_id=${entityId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const data = await response.json();
      setSchema(data.data);

      const initialFormData = {};
      const columns = data.data.schema_definition?.columns || [];
      columns.forEach((column) => {
        initialFormData[column.name] = '';
      });
      setFormData(initialFormData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name
      .toLowerCase()
      .substring(selectedFile.name.lastIndexOf('.'));

    if (!allowedTypes.includes(fileExt)) {
      setError('Please select a CSV or Excel file');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setError('');
  };

  const handleFileUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const multipart = new FormData();
      multipart.append('file', file);
      multipart.append('schema_id', schemaId);
      multipart.append('entity_id', entityId);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/visuals/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: multipart
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setSuccess(`Successfully uploaded ${data.insertedCount} records`);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onSuccess?.('upload', schemaId);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!schema) return;
    setError('');
    setSuccess('');

    const columns = schema.schema_definition?.columns || [];
    const requiredColumns = columns.filter((column) => column.required);

    for (const column of requiredColumns) {
      if (!formData[column.name]) {
        setError(`Required field '${column.name}' is missing`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          schema_id: schemaId,
          data: formData,
          entity_id: entityId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to insert data');
      }

      setSuccess('Data inserted successfully!');
      const resetForm = {};
      columns.forEach((column) => {
        resetForm[column.name] = '';
      });
      setFormData(resetForm);
      onSuccess?.('manual', schemaId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = schema?.schema_definition?.columns || [];

  if (loading) {
    return (
      <div className="schema-add-data-panel schema-panel-skeleton-wrapper">
        <div className="schema-panel-header">
          <div className="schema-skeleton-stack">
            <span className="schema-skeleton-pill skeleton-w-xs" />
            <span className="schema-skeleton-line skeleton-w-md" />
            <span className="schema-skeleton-line subtle skeleton-w-lg" />
          </div>
          <button type="button" className="schema-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="schema-panel-skeleton">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={`schema-skel-field-${idx}`} className="schema-skeleton-field">
              <span className="schema-skeleton-line skeleton-w-sm" />
              <span className="schema-skeleton-line skeleton-w-full" />
            </div>
          ))}
          <div className="schema-skeleton-actions">
            <span className="schema-skeleton-chip skeleton-w-sm" />
            <span className="schema-skeleton-chip accent skeleton-w-chip" />
          </div>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="schema-panel-error">
        <h3>Schema not found</h3>
        <p>The requested table schema could not be found.</p>
        <button type="button" className="admin-back-btn" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  const renderInput = (column) => {
    const value = formData[column.name] || '';
    const commonProps = {
      value,
      onChange: (event) => handleInputChange(column.name, event.target.value),
      className: 'form-input',
      placeholder: `Enter ${column.name}`
    };

    switch (column.type) {
      case 'integer':
        return <input type="number" {...commonProps} />;
      case 'date':
        return <input type="date" {...commonProps} />;
      case 'boolean':
        return (
          <select
            value={value}
            onChange={(event) => handleInputChange(column.name, event.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      case 'email':
        return <input type="email" {...commonProps} />;
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  return (
    <div className="schema-add-data-panel">
      {/* <div className="schema-panel-header">
        <div>
          <p className="schema-panel-kicker">Add Data</p>
          <h3>{schema.table_name}</h3>
          <p className="schema-panel-subtitle">
            {inputMode === 'manual' ? 'Fill out the form to insert a record' : 'Upload CSV or Excel files'}
          </p>
        </div>
        <button type="button" className="schema-panel-close" onClick={onClose}>
          ✕
        </button>
      </div> */}

      <div className="schema-panel-body">
        <div className="input-mode-toggle">
          <button
            type="button"
            className={`mode-btn ${inputMode === 'manual' ? 'active' : ''}`}
            onClick={() => setInputMode('manual')}
          >
            📝 Manual Entry
          </button>
          <button
            type="button"
            className={`mode-btn ${inputMode === 'upload' ? 'active' : ''}`}
            onClick={() => setInputMode('upload')}
          >
            📁 Upload File
          </button>
        </div>

        {inputMode === 'manual' ? (
          <form onSubmit={handleSubmit} className="add-data-form">
            <div className="form-section">
              <h4>Record Fields</h4>
              <div className="form-grid">
                {columns.map((column) => (
                  <div key={column.name} className="form-group">
                    <label htmlFor={column.name}>
                      {column.name}
                      {column.required && <span className="required-asterisk"> *</span>}
                    </label>
                    {renderInput(column)}
                    <div className="field-info">
                      <span className="field-type">{column.type}</span>
                      {column.required && <span className="required-badge">Required</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-actions sticky">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? 'Adding Data...' : 'Add Data'}
              </button>
            </div>
          </form>
        ) : (
          <div className="upload-section">
            <h4>Upload Data File</h4>
            <div className="upload-form">
              <div className="form-group">
                <label>Select File:</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="file-input"
                />
                <div className="file-info">
                  <p>Supported formats: CSV, Excel (.xlsx, .xls)</p>
                  <p>Maximum file size: 10MB</p>
                </div>
              </div>
            </div>
            <div className="form-actions sticky">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="submit-btn"
                onClick={handleFileUpload}
                disabled={!file || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
      </div>
    </div>
  );
};

export default SchemaAddDataPanel;

